/**
 * Pure platform detection for UI branching. Safe to call from any render
 * path. No side effects beyond reading `navigator`/`matchMedia`.
 *
 * Caller contract: these helpers are tree-shakable and stateless.
 */

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent ?? '';
  // iPhone + iPad + iPod. iPadOS 13+ spoofs as desktop Safari by default;
  // we accept that edge case (iPad-as-desktop gets the Android copy, which
  // is close enough for the install hint's purposes).
  return /iPhone|iPad|iPod/.test(ua);
}

export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent ?? '');
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS Safari uses a non-standard navigator.standalone
  const iosStandalone =
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  if (iosStandalone) return true;
  // Modern standard for everyone else (including Android Chrome PWA, desktop)
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

export interface PlatformFlags {
  isIOS: boolean;
  isAndroid: boolean;
  isStandalone: boolean;
}

export function detectPlatform(): PlatformFlags {
  return {
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isStandalone: isStandalone(),
  };
}
